import { useState } from "react";

interface UpdateSubCategoryModalProps {
    subCategory: any;
    parentCategoryName?: string;
    isLoading: boolean;
    onClose: () => void;
    onUpdate: (id: string, name: string) => void;
    onDelete: (id: string) => void;
}

const UpdateSubCategoryModal = ({
    subCategory,
    parentCategoryName,
    isLoading,
    onClose,
    onUpdate,
    onDelete,
}: UpdateSubCategoryModalProps) => {
    const [name, setName] = useState(subCategory?.name || "");
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    const handleUpdate = (e: React.FormEvent) => {
        e.preventDefault();
        if (!name.trim()) return;
        onUpdate(subCategory._id, name.trim());
    };

    return (
        <div className="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50 z-40">
            <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-md">
                <div className="flex items-center justify-between mb-5">
                    <h2 className="font-bold text-lg">Edit Subcategory</h2>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                        type="button"
                    >
                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {!showDeleteConfirm ? (
                    <form onSubmit={handleUpdate} className="space-y-4">
                        {/* Parent Category (read-only) */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Parent Category
                            </label>
                            <p className="w-full border rounded-md p-2.5 text-sm bg-gray-50 text-gray-600">
                                {parentCategoryName || "Unknown"}
                            </p>
                        </div>

                        {/* Name */}
                        <div>
                            <label htmlFor="edit-subcat-name" className="block text-sm font-medium text-gray-700 mb-1">
                                Subcategory Name
                            </label>
                            <input
                                id="edit-subcat-name"
                                type="text"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                className="w-full border rounded-md p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-secondary"
                            />
                        </div>

                        {/* Status */}
                        <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-gray-700">Status:</span>
                            <span
                                className={`text-xs px-2 py-0.5 rounded-full font-medium ${subCategory.isActive
                                        ? "bg-green-100 text-green-700"
                                        : "bg-red-100 text-red-600"
                                    }`}
                            >
                                {subCategory.isActive ? "Active" : "Inactive"}
                            </span>
                        </div>

                        {/* Actions */}
                        <div className="flex justify-between pt-2">
                            <button
                                type="button"
                                onClick={() => setShowDeleteConfirm(true)}
                                className="px-4 py-2 rounded-md bg-red-600 text-white text-sm font-medium hover:bg-red-700 transition-colors"
                            >
                                Delete
                            </button>
                            <div className="flex gap-3">
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="px-4 py-2 rounded-md border text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={isLoading || !name.trim()}
                                    className="px-4 py-2 rounded-md bg-secondary text-white text-sm font-medium hover:bg-opacity-90 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                                >
                                    {isLoading ? "Updating..." : "Update"}
                                </button>
                            </div>
                        </div>
                    </form>
                ) : (
                    /* Delete Confirmation */
                    <div className="space-y-4">
                        <div className="bg-red-50 border border-red-200 rounded-md p-4">
                            <p className="text-sm text-red-800 font-medium">
                                Are you sure you want to delete "{subCategory.name}"?
                            </p>
                            <p className="text-xs text-red-600 mt-1">
                                This action cannot be undone. Products assigned to this subcategory will be unassigned.
                            </p>
                        </div>
                        <div className="flex justify-end gap-3">
                            <button
                                type="button"
                                onClick={() => setShowDeleteConfirm(false)}
                                className="px-4 py-2 rounded-md border text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                            >
                                Go Back
                            </button>
                            <button
                                type="button"
                                disabled={isLoading}
                                onClick={() => onDelete(subCategory._id)}
                                className="px-4 py-2 rounded-md bg-red-600 text-white text-sm font-medium hover:bg-red-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                            >
                                {isLoading ? "Deleting..." : "Confirm Delete"}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default UpdateSubCategoryModal;
